/**
 * defaultDashboardData.js
 *
 * Showcases the full dashboard UI with realistic, high-performing client data.
 * Used as a fallback when the API has not yet returned real clientData
 * (e.g. new accounts still being processed, or local dev without a backend).
 *
 * Shape mirrors the API response from:
 *   POST https://server.pabloo.com/website/get_client_data
 */

const defaultDashboardData = {
    // ── Identity ────────────────────────────────────────────────────────────────
    firstName: "Sophia",
    lastName: "Martín",
    email: "sophia@bloomwellness.co",
    biz: "Bloom Wellness Co.",
    industry: "E-commerce",
    plan: "growth",
    timeline: "6 months",
    goals: ["traffic", "rank", "leads", "authority"],
    domains: ["bloomwellness.co", "bloomwellnessblog.com", "bloomwellnessshop.co"],
    invites: [
        { email: "maya@bloomwellness.co",  role: "Editor" },
        { email: "james@bloomwellness.co", role: "Viewer" },
    ],

    // ── Account-level analytics ─────────────────────────────────────────────────
    analytics: {
        security: {
            insights: 33,
            breakdown: "14 high · 19 low",
            encryptedRequests: "100.0%",
            encryptedChange: -0.0,
            trend: [60, 70, 65, 80, 75, 90, 85, 95, 88, 92, 90, 95],
        },
        performance: {
            cacheRate: "61.4%",
            cacheChange: 12.3,
            cacheTrend: [30, 38, 35, 44, 42, 50, 48, 55, 52, 58, 60, 61],
            cpuTimeP90: null,
        },
        activity: {
            webTrafficFormatted: "312.8k",
            webTrafficChange: 14.6,
            webTrend: [180, 200, 195, 220, 215, 240, 245, 260, 255, 280, 300, 312],
            workersInvocations: 4,
        },
    },

    // ── Domain detail cards ─────────────────────────────────────────────────────
    domains_detail: [
        {
            id: "bloomwellness",
            name: "bloomwellness.co",
            status: "Active",
            plan: "Growth",
            securityInsights: 14,
            uniqueVisitors: 5870000,
            uniqueVisitorsFormatted: "5.87M",
            trend: [300, 320, 310, 360, 345, 380, 370, 400, 390, 420, 415, 440],
            overallSeoScore: 91,
            seo: {
                organicTraffic:  { value: "24.1K", change: 22.4, trend: [50, 65, 60, 80, 78, 92, 90, 108, 104, 118, 115, 124] },
                keywordsRanking: { value: "412",   change: 38,   trend: [120, 140, 155, 168, 185, 210, 240, 270, 310, 355, 390, 412] },
                backlinks:       { value: "3,841", change: 8.2,  trend: [2800, 2950, 3050, 3150, 3280, 3400, 3500, 3580, 3650, 3720, 3790, 3841] },
                pageSpeed:       { value: "96",    change: 6,    trend: [76, 79, 81, 84, 86, 88, 90, 91, 93, 94, 95, 96] },
                domainAuthority: 54,
                indexedPages: 318,
                crawlErrors: 1,
                coreWebVitals: "Good",
                topKeywords: [
                    { keyword: "wellness supplements",        position: 2,  searchVolume: "18.4K", change: 4  },
                    { keyword: "natural sleep aids",          position: 4,  searchVolume: "9.2K",  change: 7  },
                    { keyword: "adaptogen benefits",          position: 1,  searchVolume: "6.1K",  change: 2  },
                    { keyword: "immune support vitamins",     position: 6,  searchVolume: "22K",   change: 3  },
                    { keyword: "best magnesium supplement",   position: 3,  searchVolume: "14.7K", change: 5  },
                    { keyword: "ashwagandha for women",       position: 5,  searchVolume: "11.3K", change: 9  },
                    { keyword: "collagen peptides benefits",  position: 8,  searchVolume: "8.8K",  change: -1 },
                    { keyword: "vitamin d deficiency signs",  position: 11, searchVolume: "31K",   change: 2  },
                ],
                issues: [
                    { type: "info",    message: "3 product images missing alt text" },
                    { type: "info",    message: "1 redirect chain detected on /supplements" },
                ],
            },
        },
        {
            id: "bloomwellnessblog",
            name: "bloomwellnessblog.com",
            status: "Active",
            plan: "Growth",
            securityInsights: 7,
            uniqueVisitors: 98400,
            uniqueVisitorsFormatted: "98.4K",
            trend: [30, 38, 35, 44, 50, 55, 60, 68, 72, 80, 88, 98],
            overallSeoScore: 78,
            seo: {
                organicTraffic:  { value: "8.3K",  change: 41.2, trend: [20, 28, 32, 38, 44, 50, 55, 62, 66, 72, 78, 83] },
                keywordsRanking: { value: "189",   change: 52,   trend: [40, 52, 65, 78, 90, 105, 118, 135, 150, 165, 178, 189] },
                backlinks:       { value: "714",   change: 18.6, trend: [350, 410, 450, 490, 520, 560, 600, 635, 660, 685, 700, 714] },
                pageSpeed:       { value: "88",    change: 5,    trend: [72, 74, 76, 78, 80, 82, 83, 84, 85, 86, 87, 88] },
                domainAuthority: 29,
                indexedPages: 94,
                crawlErrors: 0,
                coreWebVitals: "Good",
                topKeywords: [
                    { keyword: "wellness tips for women",     position: 3,  searchVolume: "7.4K",  change: 11 },
                    { keyword: "sleep hygiene routine",       position: 6,  searchVolume: "4.9K",  change: 8  },
                    { keyword: "gut health foods list",       position: 4,  searchVolume: "12.1K", change: 6  },
                    { keyword: "morning routine wellness",    position: 9,  searchVolume: "9.6K",  change: 3  },
                    { keyword: "anti-inflammatory diet plan", position: 7,  searchVolume: "5.2K",  change: 14 },
                ],
                issues: [
                    { type: "warning", message: "12 posts missing meta descriptions" },
                    { type: "info",    message: "Internal linking density low on 8 articles" },
                ],
            },
        },
        {
            id: "bloomwellnessshop",
            name: "bloomwellnessshop.co",
            status: "Active",
            plan: "Starter",
            securityInsights: 3,
            uniqueVisitors: 14200,
            uniqueVisitorsFormatted: "14.2K",
            trend: [5, 7, 8, 10, 11, 13, 12, 14, 15, 16, 15, 17],
            overallSeoScore: 64,
            seo: {
                organicTraffic:  { value: "1.4K",  change: 9.3,  trend: [6, 7, 8, 9, 10, 11, 11, 12, 12, 13, 13, 14] },
                keywordsRanking: { value: "58",    change: 18,   trend: [18, 22, 27, 30, 35, 38, 42, 46, 50, 53, 56, 58] },
                backlinks:       { value: "192",   change: 4.1,  trend: [130, 145, 152, 160, 165, 170, 175, 179, 183, 187, 190, 192] },
                pageSpeed:       { value: "74",    change: -3,   trend: [82, 80, 79, 78, 77, 76, 76, 75, 75, 74, 74, 74] },
                domainAuthority: 14,
                indexedPages: 47,
                crawlErrors: 4,
                coreWebVitals: "Needs Improvement",
                topKeywords: [
                    { keyword: "buy wellness supplements online", position: 14, searchVolume: "6.8K",  change: 5  },
                    { keyword: "natural vitamin shop",            position: 18, searchVolume: "3.1K",  change: 2  },
                    { keyword: "organic supplements store",       position: 22, searchVolume: "4.4K",  change: -2 },
                ],
                issues: [
                    { type: "warning", message: "Page speed below 80 — may impact ranking" },
                    { type: "warning", message: "4 broken links on product category pages" },
                    { type: "info",    message: "No sitemap.xml submitted to Search Console" },
                    { type: "info",    message: "Schema markup missing on 31 product pages" },
                ],
            },
        },
    ],

    // ── Workers ─────────────────────────────────────────────────────────────────
    workers: [
        { name: "image-optimizer",  type: "Worker", lastDeployed: "2 days ago"  },
        { name: "redirect-handler", type: "Worker", lastDeployed: "1 week ago"  },
        { name: "ab-test-router",   type: "Worker", lastDeployed: "3 weeks ago" },
    ],

    // ── Audit logs ──────────────────────────────────────────────────────────────
    auditLogs: [
        { action: "Keyword campaign launched",  domain: "bloomwellness.co",      time: "2h ago",  actor: "sophia@bloomwellness.co" },
        { action: "Backlink report generated",  domain: "bloomwellness.co",      time: "6h ago",  actor: "herstory-bot"            },
        { action: "New blog post indexed",      domain: "bloomwellnessblog.com", time: "1d ago",  actor: "herstory-bot"            },
        { action: "Sitemap resubmitted",        domain: "bloomwellness.co",      time: "2d ago",  actor: "maya@bloomwellness.co"   },
        { action: "Page speed optimized",       domain: "bloomwellness.co",      time: "3d ago",  actor: "herstory-bot"            },
        { action: "DNS Record updated",         domain: "bloomwellnessblog.com", time: "4d ago",  actor: "sophia@bloomwellness.co" },
        { action: "SSL certificate renewed",    domain: "bloomwellnessshop.co",  time: "5d ago",  actor: "herstory-bot"            },
        { action: "Invite Member",              domain: "—",                     time: "6d ago",  actor: "sophia@bloomwellness.co" },
    ],

    // ── Next steps ──────────────────────────────────────────────────────────────
    nextSteps: [
        { label: "Connect Google Search Console",                       done: true  },
        { label: "Invite teammates and back-up admins",                 done: true  },
        { label: "Set up your first keyword campaign",                  done: true  },
        { label: "Fix page speed issues on bloomwellnessshop.co",      done: false },
        { label: "Add schema markup to product pages",                  done: false },
        { label: "Schedule Q3 strategy workshop with your strategist",  done: false },
    ],

    // ── Onboarding checklist ─────────────────────────────────────────────────────
    onboardingChecklist: [
        { label: "Account created",            done: true  },
        { label: "3 websites added",           done: true  },
        { label: "First SEO audit complete",   done: true  },
        { label: "Met your strategist",        done: true  },
        { label: "First keyword campaign set", done: false },
    ],

    // ── Team ────────────────────────────────────────────────────────────────────
    team: [
        { email: "sophia@bloomwellness.co", role: "Admin",  status: "Active",  joinedAt: "2026-01-10" },
        { email: "maya@bloomwellness.co",   role: "Editor", status: "Active",  joinedAt: "2026-01-10" },
        { email: "james@bloomwellness.co",  role: "Viewer", status: "Active",  joinedAt: "2026-02-03" },
        { email: "priya@bloomwellness.co",  role: "Viewer", status: "Pending", joinedAt: null         },
    ],

    // ── Account meta ────────────────────────────────────────────────────────────
    accountMeta: {
        plan: "Growth",
        monthlyRate: 1997,
        nextBillingDate: "2026-06-10",
    },
};

export default defaultDashboardData;
